import "react-native-gesture-handler";
import "../global.css";

import { Stack } from "expo-router";
import Head from "expo-router/head";
import { Platform } from "react-native";

import { CookieConsentBanner } from "../components/CookieConsentBanner";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { AuthProvider } from "../lib/authContext";
import { initAnalytics } from "../lib/analytics";
import {
  canonicalUrl,
  MARKETING_META_DESCRIPTION,
  MARKETING_PAGE_TITLE,
  META_LANDING,
  ogImageDimensions,
  ogImageUrl,
} from "../lib/marketingMeta";
import { initSentry } from "../lib/sentry";
import { useDynamicFavicon } from "../lib/useDynamicFavicon";

// Web-only: the Coravu wheel cursor is mounted at the root so it persists
// across navigation and renders on top of every page. The component is a
// noop on native, but we still gate the import behind Platform to avoid
// pulling reanimated into native bundles for a feature that's web-only.
const CursorFollower =
  Platform.OS === "web"
    ? // eslint-disable-next-line @typescript-eslint/no-require-imports
      (require("../components/CursorFollower").CursorFollower as React.ComponentType)
    : null;

// Initialize observability synchronously at module evaluation time. Both
// init helpers are idempotent and SSR-safe (no-op when window is undefined),
// so doing this at import time means the very first error or page-view —
// even ones that fire before RootLayout mounts — gets captured. The DSN /
// key are read from EXPO_PUBLIC_* env vars; missing is a graceful no-op.
initSentry();
initAnalytics();

function RootWebHead() {
  if (Platform.OS !== "web") return null;
  // The OG/Twitter tags here are the *static-HTML* defaults that
  // social-card scrapers see (they don't execute JS, so they never see
  // the per-page <MarketingHead /> updates). Use the landing copy +
  // canonical OG image as the universal fallback — every public link
  // shared on Slack/LinkedIn/Twitter shows the Coravu landing card,
  // which is fine for v1. Browser users still get the page-specific
  // meta from MarketingHead at runtime. To get true per-page previews
  // for scrapers, switch app.json's `web.output` to "static" so each
  // route prerenders its own HTML.
  const og = META_LANDING;
  const ogUrl = canonicalUrl(og.pathname);
  const ogImg = ogImageUrl();
  const { width, height } = ogImageDimensions();
  return (
    <Head>
      <title>{MARKETING_PAGE_TITLE}</title>
      <meta name="description" content={MARKETING_META_DESCRIPTION} />

      {/* OpenGraph (universal landing defaults — per-page MarketingHead
       *  updates these at runtime for browser users). */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Coravu" />
      <meta property="og:title" content={og.title} />
      <meta property="og:description" content={og.description} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={ogImg} />
      <meta property="og:image:width" content={width} />
      <meta property="og:image:height" content={height} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={og.title} />
      <meta name="twitter:description" content={og.description} />
      <meta name="twitter:image" content={ogImg} />

      {/* useDynamicFavicon swaps the per-route SVG icon and strips other
       *  `<link rel="icon">` nodes — see the hook for why. So we set the
       *  initial SVG link here for first-paint coverage, but rely on the
       *  hook for route-aware updates. apple-touch-icon + manifest +
       *  theme-color use distinct selectors and survive the strip. */}
      <link rel="icon" type="image/svg+xml" href="/favicons/public.svg" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicons/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />
      <meta name="theme-color" content="#080808" />
    </Head>
  );
}

function DynamicFavicon() {
  useDynamicFavicon();
  return null;
}

export default function RootLayout() {
  return (
    <ErrorBoundary boundaryName="root">
      <AuthProvider>
        <RootWebHead />
        <DynamicFavicon />
        {CursorFollower ? <CursorFollower /> : null}
        <Stack screenOptions={{ headerShown: false }} />
        <CookieConsentBanner />
      </AuthProvider>
    </ErrorBoundary>
  );
}
